/**
 * Hierarchische Ansicht einer Akte (Haupt-Akte + Sub-Akten mit Dokumenten).
 *
 * Rendert dieselbe Baum-UI fuer Mitarbeiter- und Kunden-Akten. Nutzt die
 * bibliothek-Backend-Endpunkte:
 *   - GET /akten/by-person/:personId/struktur     (Mitarbeiter)
 *   - GET /akten/by-customer/:customerId/struktur (Kunden)
 *
 * ACL pro Sub-Ordner ist ueber das Schild-Icon editierbar (Admin/GF/Buero),
 * PUT /akten/:id/acl. null/null = Reset, Vererbung greift wieder.
 *
 * Verwendungen:
 *  - AdressbuchPersonAkte.tsx (Mitarbeiter-Sub-Tab „Akte")
 *  - Eingebetteter Kunde-Akte-Tab im Adressbuch-Kontakt-Detail (P4.3)
 *  - Legacy CustomerDetailPage (uebergangsweise)
 */
import { useCallback, useEffect, useState, type ComponentType } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Search } from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';

import AktenAclEditModal from './AktenAclEditModal';
import { useFederatedModule } from '../lib/useFederatedModule';

// Jörg 2026-09-07 (Foto-Flow-Abnahme: „Ich sehe nur ein weißes Fenster und
// einen Download. Ich brauche eine Seite die das Bild und den erkannten Text
// anzeigt"): Bilder öffnen im bibliothek-Viewer (Lightbox, kind='akte' zeigt
// zusätzlich extracted_text) statt als Download-Link. Dynamisch geladen —
// Regel [[feedback_federation_no_static_imports]].
type BibliothekPublic = {
  ImageLightbox: ComponentType<{ docId: string; title: string | null; kind?: 'library' | 'akte'; onClose: () => void }>;
};

interface AkteDocument {
  id: string;
  title: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

interface AkteSubStruct {
  id: string;
  name: string;
  readers: string[] | null;
  authors: string[] | null;
  document_count: number;
  documents: AkteDocument[];
}

interface AkteStrukturData {
  haupt: { id: string; name: string; readers: string[] | null; authors: string[] | null };
  /** Suchbegriff, mit dem die Dokumente gefiltert wurden (bibliothek 1.13.0; leer = alle). */
  query?: string;
  subakten: AkteSubStruct[];
}

export type AkteSource =
  | { kind: 'person'; personId: string }
  | { kind: 'customer'; customerId: string };

interface Props {
  source: AkteSource;
  /** Konfigurations-Link im Fuss der Haupt-Karte. */
  configTyp: 'kunde' | 'mitarbeiter';
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function mimeToIcon(mime: string): string {
  if (mime.startsWith('image/')) return '🖼️';
  if (mime === 'application/pdf') return '📄';
  if (mime.includes('word') || mime.includes('officedocument.wordprocessingml')) return '📝';
  if (mime.includes('sheet') || mime.includes('excel')) return '📊';
  if (mime.startsWith('text/')) return '📃';
  return '📎';
}

function buildEndpoint(source: AkteSource): string {
  return source.kind === 'person'
    ? `/modules/bibliothek/akten/by-person/${source.personId}/struktur`
    : `/modules/bibliothek/akten/by-customer/${source.customerId}/struktur`;
}

export default function AkteStrukturViewer({ source, configTyp }: Props) {
  const { isAdmin, user } = useAuth();
  const canEditAcl = isAdmin || user?.role === 'gf' || user?.role === 'buero';
  const [data, setData] = useState<AkteStrukturData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aclEditId, setAclEditId] = useState<string | null>(null);
  const [aclSaving, setAclSaving] = useState(false);
  const bibliothek = useFederatedModule<BibliothekPublic>('bibliothek/public');
  const ImageLightbox = bibliothek?.ImageLightbox ?? null;
  const [lightbox, setLightbox] = useState<{ id: string; title: string | null } | null>(null);

  const endpoint = buildEndpoint(source);

  // Fund 3 (Jörg 2026-10-06: „mir fehlt die Suche in der Kundenakte"): Suchfeld über der
  // Struktur, serverseitig (?q= über Titel, Dateiname, Volltext — bibliothek 1.13.0).
  // 300 ms Verzögerung; die Struktur bleibt beim Tippen stehen, nur die Dokumente wechseln.
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);

  const reload = useCallback((term: string = '') => {
    setSearching(true); // Erstladen zeigt „Lädt…" über `loading` (Startwert true), danach nur den Suchhinweis
    setError(null);
    const url = term ? `${endpoint}?q=${encodeURIComponent(term)}` : endpoint;
    api.get<{ success: boolean; data: AkteStrukturData }>(url)
      .then((r) => setData(r.data))
      .catch((e: unknown) => setError((e as Error).message))
      .finally(() => { setLoading(false); setSearching(false); });
  }, [endpoint]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const t = window.setTimeout(() => reload(q.trim()), 300);
    return () => window.clearTimeout(t);
  }, [q, reload]);

  async function saveAcl(subId: string, readers: string[] | null, authors: string[] | null) {
    setAclSaving(true);
    try {
      const r = await fetch(`/api/modules/bibliothek/akten/${subId}/acl`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ readers, authors }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error ?? `HTTP ${r.status}`);
      setAclEditId(null);
      reload();
    } finally {
      setAclSaving(false);
    }
  }

  if (loading) return <div className="text-sm text-slate-500">Lädt Akte-Struktur…</div>;
  if (error) return <div className="text-sm text-rose-400">Fehler: {error}</div>;
  if (!data) return <div className="text-sm text-slate-500">Keine Struktur vorhanden.</div>;

  const totalDocs = data.subakten.reduce((sum, s) => sum + s.document_count, 0);
  const shownDocs = data.subakten.reduce((sum, s) => sum + s.documents.length, 0);
  const isSearch = q.trim() !== '';
  const configLink = `/m/vorlagen/aktenstruktur?typ=${configTyp}`;
  const configLabel = configTyp === 'kunde' ? 'Kunden-Akten' : 'Mitarbeiter-Akten';

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">
            📁 {data.haupt.name}
          </h3>
          <span className="text-xs text-slate-500">
            {data.subakten.length} Kategorien · {totalDocs} Dokumente
          </span>
        </div>
        <p className="text-xs text-slate-500">
          Kategorien und Berechtigungen konfiguriert unter{' '}
          <Link to={configLink} className="text-brand-400 hover:text-brand-300 underline">
            Vorlagen → Aktenstruktur → {configLabel}
          </Link>.
        </p>
        <div className="mt-3 flex items-center gap-2" data-test-anchor="akte-suche">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="In der Akte suchen — Titel, Dateiname, Inhalt"
              aria-label="In der Akte suchen"
              className="w-full rounded-lg border border-slate-700 bg-slate-900 pl-8 pr-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-brand-500"
            />
          </div>
          {q.trim() !== '' && (
            <span className="text-xs text-slate-400 whitespace-nowrap" data-test-anchor="akte-suche-treffer">
              {searching ? 'Suche…' : `${shownDocs} Treffer`}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {data.subakten.length === 0 && (
          <div className="text-sm text-slate-500 italic px-4 py-3">
            Noch keine Sub-Akten. Der Admin muss Kategorien in der Aktenstruktur pflegen.
          </div>
        )}
        {data.subakten.map((s) => {
          const readerCount = (s.readers ?? []).length;
          const authorCount = (s.authors ?? []).length;
          return (
            <details key={isSearch ? `${s.id}-s` : s.id} open={isSearch && s.documents.length > 0 ? true : undefined} className="rounded-lg border border-slate-800 bg-slate-900/30 open:bg-slate-900/50">
              <summary className="cursor-pointer px-4 py-2.5 flex items-center justify-between gap-2 text-sm hover:bg-slate-800/40 rounded-lg">
                <span className="flex items-center gap-2 min-w-0">
                  <span>📂</span>
                  <span className="text-slate-100 truncate">{s.name}</span>
                  <span className="text-xs text-slate-500 flex-shrink-0">({s.document_count} Dok.)</span>
                </span>
                <span className="flex gap-1.5 flex-shrink-0 items-center">
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700"
                    title={`Reader-ACL: ${readerCount} Gruppen/Personen`}
                  >
                    👁 {readerCount || '—'}
                  </span>
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700"
                    title={`Author-ACL: ${authorCount} Gruppen/Personen`}
                  >
                    ✍ {authorCount || '—'}
                  </span>
                  {canEditAcl && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setAclEditId(s.id); }}
                      className="p-1 rounded text-brand-400 hover:text-brand-300 hover:bg-slate-800"
                      title="Berechtigungen für diesen Ordner bearbeiten"
                    >
                      <Shield size={13} />
                    </button>
                  )}
                </span>
              </summary>
              <div className="px-4 pb-3 pt-1">
                {s.documents.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">{isSearch ? 'Kein Treffer in dieser Kategorie.' : 'Keine Dokumente in dieser Kategorie.'}</p>
                ) : (
                  <ul className="space-y-1">
                    {s.documents.map((d) => (
                      <li key={d.id} className="flex items-center gap-2 text-xs py-1 hover:bg-slate-800/30 px-2 rounded">
                        <span>{mimeToIcon(d.mime_type)}</span>
                        {d.mime_type.startsWith('image/') && ImageLightbox ? (
                          <button
                            type="button"
                            onClick={() => setLightbox({ id: d.id, title: d.title })}
                            className="text-slate-200 hover:text-brand-300 truncate flex-1 text-left"
                          >
                            {d.title}
                          </button>
                        ) : (
                          <a
                            href={`/api/modules/bibliothek/akten-documents/${d.id}/inline`}
                            className="text-slate-200 hover:text-brand-300 truncate flex-1"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {d.title}
                          </a>
                        )}
                        <span className="text-slate-500 flex-shrink-0">{fmtBytes(d.size_bytes)}</span>
                        <span className="text-slate-500 flex-shrink-0 whitespace-nowrap">
                          {new Date(d.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </details>
          );
        })}
      </div>

      {aclEditId && (() => {
        const sub = data.subakten.find((x) => x.id === aclEditId);
        if (!sub) return null;
        return (
          <AktenAclEditModal
            open
            title={`Berechtigungen — „${sub.name}"`}
            readers={sub.readers}
            authors={sub.authors}
            allowReset
            saving={aclSaving}
            onClose={() => setAclEditId(null)}
            onSave={(readers, authors) => saveAcl(sub.id, readers, authors)}
          />
        );
      })()}
      {lightbox && ImageLightbox && (
        <ImageLightbox kind="akte" docId={lightbox.id} title={lightbox.title} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}
